import moment from 'moment';
import React, { useRef } from 'react';
import { Button, Card, CardBody, CardFooter, CardHeader, Input, Table } from 'reactstrap';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';

const InspectionReport = ({ data }) => {
    const reportRef = useRef();

    // Export PDF
    const exportPDF = async () => {
      const input = reportRef.current;
      const canvas = await html2canvas(input);
      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save('inspection_report.pdf');
    };

    // Export Excel
    const exportExcel = () => {
      const workbook = XLSX.utils.book_new();

      // Iterate over each checkpoint and add it as a separate sheet
      data.forEach((checkpoint, chkIndex) => {
          const sheetData = [];

          // Add checkpoint header
          sheetData.push([
              `CHECKPOINT ${chkIndex + 1}: ${checkpoint.checkpoint_name}`,
              '', '', '', '', ''
          ]);

          // Add the date and time row
          sheetData.push([
              'Inspection Date:',
              moment(checkpoint.created_at).format('D/M/YYYY'),
              'Time:',
              moment(checkpoint.created_at).format('HH:mm:ssa'),
              '', ''
          ]);

          // Add table headers
          sheetData.push([
              'INSPECTION CHECK LIST:', 'COMPONENT', 'TROUBLESHOOT', 'REMARKS', 'DEFECT', 'NO DEFECT'
          ]);

          // Populate table rows with component data
          checkpoint.categories.forEach((category) => {
              category.components.forEach((component, compIndex) => {
                  const row = [
                      compIndex === 0 ? category.category : '',
                      component.name,
                      component?.troubleshoot?.activity || 'N/A',
                      component.remarks,
                      component.checkpoints[0]?.defect ? 'Yes' : '',
                      !component.checkpoints[0]?.defect ? 'Yes' : ''
                  ];
                  sheetData.push(row);
              });
          });

          // Convert the sheet data into a worksheet
          const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
          XLSX.utils.book_append_sheet(workbook, worksheet, `Checkpoint ${chkIndex + 1}`);
      });

      // Save the workbook as an Excel file
      XLSX.writeFile(workbook, `Inspection_Report_${moment().format('YYYYMMDD')}.xlsx`);
  };


    // Print Report
    const printReport = () => {
      const content = reportRef.current.innerHTML;
      const printWindow = window.open('', '', 'width=800,height=600');
      printWindow.document.write(`
        <html>
          <head>
            <title>Inspection Report</title>
            <style>
              /* Add any necessary styles for printing */
              body { font-family: Arial, sans-serif; }
              table { width: 100%; border-collapse: collapse; }
              th, td { border: 1px solid black; padding: 8px; text-align: left; }
              th { background-color: #f2f2f2; }
            </style>
          </head>
          <body>
            ${content}
          </body>
        </html>
      `);
      printWindow.document.close(); // close the document to ensure it's fully written before printing
      printWindow.focus(); // focus the window before printing
      printWindow.print(); // trigger the print dialog
    };
    

    return (
      <div className="inspection-report-container">

        <div className="d-flex justify-content-end mb-3">
          <Button size='sm' color="primary" className="me-2" onClick={exportPDF}>
            <span className='ri-file-pdf-line'></span> Download PDF
          </Button>
          <Button size='sm' color="primary" className="me-2" onClick={exportExcel}>
            <span className='ri-file-excel-line'></span> Download Excel
          </Button>
          <Button size='sm' color="primary" onClick={printReport}>
            <span className='ri-printer-line'></span> Print
          </Button>
        </div>

        <div ref={reportRef}>
          {data?.map((checkpoint, chkIndex) => (
            <Card key={checkpoint.checkpoint_id} className="shadow-sm mb-3">
              <CardHeader className="bg-lighta">
                <h4 className="mb-0">CHECKPOINT {chkIndex + 1}: {checkpoint.checkpoint_name}</h4>
              </CardHeader>
              <CardBody>
                <div className="d-flex justify-content-between mb-3">
                  <h5 className="fw-bold">Inspection Date:</h5>
                  <p className="text-muted">
                    <span>Date: {`${moment(checkpoint.created_at).format('D/M/YYYY')} (${moment(checkpoint.created_at).startOf().fromNow()})`} </span>
                    <span className='ms-1'>Time: {moment(checkpoint.created_at).format('HH:mm:ssa')}</span>
                  </p>
                </div>
                <div className='table-responsive table-card'>
                  <Table bordered size="sm">
                    <thead className='bg-light'>
                      <tr>
                        <th>INSPECTION CHECK LIST:</th>
                        <th>COMPONENT</th>
                        <th>TROUBLESHOOT</th>
                        <th>REMARKS</th>
                        <th className='text-center'>DEFECT</th>
                        <th className='text-center'>NO DEFECT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {checkpoint.categories.map((category, catIndex) => (
                        <React.Fragment key={catIndex}>
                          {category.components.map((component, compIndex) => (
                            <tr key={compIndex}>
                              {compIndex === 0 && (
                                <td rowSpan={category.components.length}>
                                  {category.category}
                                </td>
                              )}
                              <td>{component.name}</td>
                              <td>
                                {component?.troubleshoot?.activity || (
                                  <small className='text-muted'>N/A</small>
                                )}
                              </td>
                              <td>{component.remarks}</td>
                              <td>
                                <div className='d-flex justify-content-center'>
                                    <Input
                                        type="checkbox"
                                        checked={component.checkpoints[0]?.defect}
                                        style={{
                                          backgroundColor:component.checkpoints[0]?.defect && 'red',
                                          borderColor:component.checkpoints[0]?.defect && 'red'}}
                                        readOnly
                                        name={`defect-${catIndex}-${compIndex}-${chkIndex}`}
                                    />
                                </div>
                              </td>
                              <td>
                                <div className='d-flex justify-content-center'>
                                    <Input
                                        type="checkbox"
                                        checked={!component.checkpoints[0]?.defect}
                                        style={{
                                          backgroundColor:!component.checkpoints[0]?.defect && '#020087',
                                          borderColor:!component.checkpoints[0]?.defect && '#020087'}}
                                        readOnly
                                        name={`noDefect-${catIndex}-${compIndex}-${chkIndex}`}
                                    />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </Table>
                </div>
                
              </CardBody>
              <CardFooter>
                  <div>
                      <h6>INSPECTION OFFICER : {checkpoint.created_by_name}</h6>
                  </div>
              </CardFooter>
            </Card>
          ))}
        </div>


        
      </div>
    );
  };
  

export default InspectionReport;